import { useReducer, useEffect, useRef, useState } from 'preact/hooks';
import {
  openDb,
  getSettings,
  putSettings,
  addContribution,
  getAllContributions,
  addWithdrawal,
  getAllWithdrawals,
  deleteContribution,
  deleteWithdrawal,
  updateContribution,
  updateWithdrawal,
  clearAllData,
  type HBFDatabase,
} from './db';
import { floorAmountCents } from './clamp';
import type { AppState, Action } from './state';

type Reducer<S, A> = (state: S, action: A) => S;

export interface PersistedReducerResult {
  state: AppState;
  dispatch: (action: Action) => void;
  hydrated: boolean;
}

export function usePersistedReducer(
  reducer: Reducer<AppState, Action>,
  initial: AppState
): PersistedReducerResult {
  const [state, rawDispatch] = useReducer(reducer, initial);
  const [hydrated, setHydrated] = useState(false);
  const dbRef = useRef<HBFDatabase | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const db = await openDb();
      if (cancelled) { db.close(); return; }
      dbRef.current = db;

      const settings = await getSettings(db);
      const contribs = await getAllContributions(db);
      const withdrawals = await getAllWithdrawals(db);

      if (cancelled) return;

      // Always hydrate — even when settings is null, contributions may exist
      const wizardInputs = settings?.wizard_completed_at ? {
        birth_year: settings.birth_year!,
        residency_year: settings.residency_year!,
        cra_room_method: settings.cra_room_method!,
        cra_room_initial_cents: settings.cra_room_initial_cents!,
        cra_verified_at_iso: settings.cra_verified_at_iso,
        contributions_ytd_cents: settings.contributions_ytd_cents,
        withdrawals_ytd_cents: settings.withdrawals_ytd_cents,
      } : null;

      // v1.0.4 P0 (defect C, layer 4 — hydration clamp): legacy IDB
      // records from v1.0.3 users may contain amount_cents='-500000' or
      // other negatives because the input layer wasn't clamping. Floor
      // on read so a stale bad record can't infect the in-memory state.
      const logEntries = [
        ...contribs.map(c => ({
          id: c.id,
          type: 'contribution' as const,
          date: c.date,
          amount_cents: floorAmountCents(c.amount_cents),
          ...(c.institution ? { institution: c.institution } : {}),
          created_at: c.created_at,
        })),
        ...withdrawals.map(w => ({
          id: w.id,
          type: 'withdrawal' as const,
          date: w.date,
          amount_cents: floorAmountCents(w.amount_cents),
          ...(w.institution ? { institution: w.institution } : {}),
          created_at: w.created_at,
        })),
      ].sort((a, b) => a.created_at.localeCompare(b.created_at));

      // Only dispatch if IDB has meaningful data (avoids clobbering initial state
      // on a truly fresh install where all stores are empty)
      if (settings !== null || logEntries.length > 0) {
        rawDispatch({
          type: 'HYDRATE',
          state: {
            disclaimerAccepted: !!(settings?.disclaimer_accepted_at),
            wizardCompleted: !!(settings?.wizard_completed_at),
            wizardInputs,
            logEntries,
            constantsVersion: initial.constantsVersion,
            constantsVerifiedAt: initial.constantsVerifiedAt,
          },
        });
      }

      setHydrated(true);
    })();

    return () => {
      cancelled = true;
      if (dbRef.current) {
        dbRef.current.close();
        dbRef.current = null;
      }
    };
  }, []);

  function dispatch(action: Action): void {
    rawDispatch(action);

    void (async () => {
      const db = dbRef.current;
      if (!db) return;

      if (action.type === 'ACCEPT_DISCLAIMER') {
        const current = await getSettings(db);
        const base = current ?? emptySettings();
        await putSettings(db, { ...base, disclaimer_accepted_at: new Date().toISOString() });
      }

      if (action.type === 'COMPLETE_WIZARD') {
        const current = await getSettings(db);
        const base = current ?? emptySettings();
        await putSettings(db, {
          ...base,
          birth_year: action.inputs.birth_year,
          residency_year: action.inputs.residency_year,
          cra_room_initial_cents: action.inputs.cra_room_initial_cents,
          cra_room_method: action.inputs.cra_room_method,
          cra_verified_at_iso: action.inputs.cra_verified_at_iso ?? null,
          contributions_ytd_cents: action.inputs.contributions_ytd_cents,
          withdrawals_ytd_cents: action.inputs.withdrawals_ytd_cents,
          wizard_completed_at: new Date().toISOString(),
        });
      }

      if (action.type === 'ADD_ENTRY') {
        const created_at = new Date().toISOString();
        if (action.entry.type === 'contribution') {
          await addContribution(db, {
            date: action.entry.date,
            amount_cents: action.entry.amount_cents,
            ...(action.entry.institution ? { institution: action.entry.institution } : {}),
            created_at,
          });
          // Increment backup counter for contributions only
          const settingsNow = await getSettings(db);
          if (settingsNow) {
            await putSettings(db, {
              ...settingsNow,
              contributions_since_last_backup: settingsNow.contributions_since_last_backup + 1,
            });
          }
        } else {
          await addWithdrawal(db, {
            date: action.entry.date,
            amount_cents: action.entry.amount_cents,
            ...(action.entry.institution ? { institution: action.entry.institution } : {}),
            created_at,
          });
        }
      }

      if (action.type === 'DELETE_ENTRY') {
        if (action.entry_type === 'contribution') {
          await deleteContribution(db, action.id);
        } else {
          await deleteWithdrawal(db, action.id);
        }
      }

      // v1.0.3 P1: edit a logged entry. Without this hook the in-memory
      // edit was lost on the next mount (smoke S21 FAIL, 2026-05-20).
      if (action.type === 'UPDATE_ENTRY') {
        if (action.entry_type === 'contribution') {
          await updateContribution(db, action.id, action.patch);
        } else {
          await updateWithdrawal(db, action.id, action.patch);
        }
      }

      if (action.type === 'RESET') {
        await clearAllData(db);
      }
    })();
  }

  return { state, dispatch, hydrated };
}

function emptySettings() {
  return {
    birth_year: null as null,
    residency_year: null as null,
    cra_room_initial_cents: null as null,
    cra_room_method: null as null,
    cra_verified_at_iso: null as null,
    contributions_ytd_cents: '0',
    withdrawals_ytd_cents: '0',
    wizard_completed_at: null as null,
    disclaimer_accepted_at: null as null,
    last_backup_export_at: null as null,
    contributions_since_last_backup: 0,
  };
}
